import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpIdTagsOptions } from '@onefootprint/fetch-dashboard';
import { IcoDotsHorizontal16 } from '@onefootprint/icons';
import { Dropdown, IconButton, useShortcut } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEntityId from '@/entity/hooks/use-entity-id';
import useBusinessOwners from '@/hooks/use-business-owners';
import usePermissions from '@/hooks/use-permissions';
import useSession from '@/hooks/use-session';
const EditTagsDialog = dynamic(() => import('../edit-tags-dialog'));
const EditVaultDrawer = dynamic(() => import('../edit-vault-dialog'));
const RunVerificationChecksDialog = dynamic(() => import('../run-verification-checks-dialog'));
const UploadDocDialog = dynamic(() => import('../upload-doc-dialog'));
const RequestMoreInfo = dynamic(() => import('./components/request-more-info'));
const RunPlaybookDialog = dynamic(() => import('../run-playbook-dialog'));
const DeactivateEntityDialog = dynamic(() => import('../deactivate-entity-dialog'));
const RedactDialog = dynamic(() => import('../redact-dialog'));
enum ActionDialog {
  EditTags = 'edit-tags',
  EditVault = 'edit-vault',
  RequestMoreInfo = 'request-more-info',
  RunPlaybook = 'run-playbook',
  UploadDoc = 'upload-doc',
  RunVerificationChecks = 'run-verification-checks',
  DeactivateEntity = 'deactivate-entity',
  Redact = 'redact',
  RedactDocumentImages = 'redact-document-images',
}
const SHORTCUTS: Omit<Record<ActionDialog, string[]>, ActionDialog.DeactivateEntity | ActionDialog.Redact | ActionDialog.RedactDocumentImages> = {
  [ActionDialog.EditTags]: ['Control', 'e'],
  [ActionDialog.EditVault]: ['Control', 'v'],
  [ActionDialog.UploadDoc]: ['Control', 'u'],
  [ActionDialog.RunVerificationChecks]: ['Control', 'j'],
  [ActionDialog.RequestMoreInfo]: ['Control', 'k'],
  [ActionDialog.RunPlaybook]: ['Control', 'r']
};
const BusinessActions = () => {
  const $ = _c(117);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "actions"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("business-details", t0);
  const entityId = useEntityId();
  let t1;
  if ($[1] !== entityId) {
    t1 = getEntitiesByFpIdTagsOptions({
      path: {
        fpId: entityId
      }
    });
    $[1] = entityId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = !!entityId;
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = {
      ...t1,
      enabled: t2
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const tagsQuery = useQuery(t3);
  const bosQuery = useBusinessOwners(entityId);
  const [openDialog, setOpenDialog] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const {
    hasPermission
  } = usePermissions();
  const {
    data: t4
  } = useSession();
  const {
    user
  } = t4;
  const hasBusinessOwners = !!bosQuery.data?.length;
  const isPending = tagsQuery.isPending || bosQuery.isPending;
  const isRiskOrCxOpsFirmEmployee = !!user?.isFirmEmployee && !!(user?.isRiskOpsEmployee || user?.isCxOpsEmployee);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setOpenDialog(null);
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleCloseDialog = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = dialog => () => {
      setDropdownOpen(false);
      setOpenDialog(dialog);
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleDialogOpen = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => handleDialogOpen(ActionDialog.EditVault)();
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleShortcutEditVault = t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => handleDialogOpen(ActionDialog.EditTags)();
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const handleShortcutEditTags = t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => handleDialogOpen(ActionDialog.UploadDoc)();
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const handleShortcutUploadDoc = t9;
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => handleDialogOpen(ActionDialog.RunVerificationChecks)();
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const handleShortcutRunVerificationChecks = t10;
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => handleDialogOpen(ActionDialog.RequestMoreInfo)();
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const handleShortcutRequestMoreInfo = t11;
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => handleDialogOpen(ActionDialog.RunPlaybook)();
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const handleShortcutRunPlaybook = t12;
  let t13;
  if ($[14] !== hasPermission) {
    t13 = hasPermission("write_entities");
    $[14] = hasPermission;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  const t14 = !t13;
  let t15;
  if ($[16] !== t14) {
    t15 = {
      keys: SHORTCUTS[ActionDialog.EditVault],
      onShortcut: handleShortcutEditVault,
      disabled: t14
    };
    $[16] = t14;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  useShortcut(t15);
  let t16;
  if ($[18] !== hasPermission) {
    t16 = hasPermission("label_and_tag");
    $[18] = hasPermission;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  const t17 = !t16;
  let t18;
  if ($[20] !== t17) {
    t18 = {
      keys: SHORTCUTS[ActionDialog.EditTags],
      onShortcut: handleShortcutEditTags,
      disabled: t17
    };
    $[20] = t17;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  useShortcut(t18);
  let t19;
  if ($[22] !== hasPermission) {
    t19 = hasPermission("write_entities");
    $[22] = hasPermission;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  const t20 = !t19;
  let t21;
  if ($[24] !== t20) {
    t21 = {
      keys: SHORTCUTS[ActionDialog.UploadDoc],
      onShortcut: handleShortcutUploadDoc,
      disabled: t20
    };
    $[24] = t20;
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  useShortcut(t21);
  let t22;
  if ($[26] !== hasPermission) {
    t22 = hasPermission("write_entities");
    $[26] = hasPermission;
    $[27] = t22;
  } else {
    t22 = $[27];
  }
  const t23 = !t22;
  let t24;
  if ($[28] !== t23) {
    t24 = {
      keys: SHORTCUTS[ActionDialog.RunVerificationChecks],
      onShortcut: handleShortcutRunVerificationChecks,
      disabled: t23
    };
    $[28] = t23;
    $[29] = t24;
  } else {
    t24 = $[29];
  }
  useShortcut(t24);
  let t25;
  if ($[30] !== hasPermission) {
    t25 = hasPermission("manual_review");
    $[30] = hasPermission;
    $[31] = t25;
  } else {
    t25 = $[31];
  }
  const t26 = !t25;
  let t27;
  if ($[32] !== t26) {
    t27 = {
      keys: SHORTCUTS[ActionDialog.RequestMoreInfo],
      onShortcut: handleShortcutRequestMoreInfo,
      disabled: t26
    };
    $[32] = t26;
    $[33] = t27;
  } else {
    t27 = $[33];
  }
  useShortcut(t27);
  let t28;
  if ($[34] !== hasPermission) {
    t28 = hasPermission("manual_review");
    $[34] = hasPermission;
    $[35] = t28;
  } else {
    t28 = $[35];
  }
  const t29 = !t28;
  let t30;
  if ($[36] !== t29) {
    t30 = {
      keys: SHORTCUTS[ActionDialog.RunPlaybook],
      onShortcut: handleShortcutRunPlaybook,
      disabled: t29
    };
    $[36] = t29;
    $[37] = t30;
  } else {
    t30 = $[37];
  }
  useShortcut(t30);
  let t31;
  if ($[38] !== t) {
    t31 = t(_temp);
    $[38] = t;
    $[39] = t31;
  } else {
    t31 = $[39];
  }
  let t32;
  if ($[40] !== isPending || $[41] !== t31) {
    t32 = <Dropdown.Trigger asChild={true}><div><IconButton variant="outline" aria-label={t31} size="compact" disabled={isPending} icon={IcoDotsHorizontal16} /></div></Dropdown.Trigger>;
    $[40] = isPending;
    $[41] = t31;
    $[42] = t32;
  } else {
    t32 = $[42];
  }
  let t33;
  if ($[43] !== t) {
    t33 = t(_temp2);
    $[43] = t;
    $[44] = t33;
  } else {
    t33 = $[44];
  }
  let t34;
  if ($[45] !== t33) {
    t34 = <Dropdown.GroupTitle>{t33}</Dropdown.GroupTitle>;
    $[45] = t33;
    $[46] = t34;
  } else {
    t34 = $[46];
  }
  let t35;
  if ($[47] !== hasPermission || $[48] !== t) {
    t35 = hasPermission("write_entities") && <Dropdown.Item onSelect={handleDialogOpen(ActionDialog.EditVault)} shortcutHint={SHORTCUTS[ActionDialog.EditVault]}>{t(_temp3)}</Dropdown.Item>;
    $[47] = hasPermission;
    $[48] = t;
    $[49] = t35;
  } else {
    t35 = $[49];
  }
  let t36;
  if ($[50] !== hasPermission || $[51] !== t || $[52] !== tagsQuery.data) {
    t36 = hasPermission("label_and_tag") && tagsQuery.data && <Dropdown.Item onSelect={handleDialogOpen(ActionDialog.EditTags)} shortcutHint={SHORTCUTS[ActionDialog.EditTags]}>{tagsQuery.data.length > 0 ? t(_temp4) : t(_temp5)}</Dropdown.Item>;
    $[50] = hasPermission;
    $[51] = t;
    $[52] = tagsQuery.data;
    $[53] = t36;
  } else {
    t36 = $[53];
  }
  let t37;
  if ($[54] !== hasPermission || $[55] !== t) {
    t37 = hasPermission("write_entities") && <Dropdown.Item onSelect={handleDialogOpen(ActionDialog.UploadDoc)} shortcutHint={SHORTCUTS[ActionDialog.UploadDoc]}>{t(_temp6)}</Dropdown.Item>;
    $[54] = hasPermission;
    $[55] = t;
    $[56] = t37;
  } else {
    t37 = $[56];
  }
  let t38;
  if ($[57] !== hasPermission || $[58] !== t) {
    t38 = hasPermission("write_entities") && <Dropdown.Item onSelect={handleDialogOpen(ActionDialog.RunVerificationChecks)} shortcutHint={SHORTCUTS[ActionDialog.RunVerificationChecks]}>{t(_temp7)}</Dropdown.Item>;
    $[57] = hasPermission;
    $[58] = t;
    $[59] = t38;
  } else {
    t38 = $[59];
  }
  let t39;
  if ($[60] !== t34 || $[61] !== t35 || $[62] !== t36 || $[63] !== t37 || $[64] !== t38) {
    t39 = <Dropdown.Group>{t34}{t35}{t36}{t37}{t38}</Dropdown.Group>;
    $[60] = t34;
    $[61] = t35;
    $[62] = t36;
    $[63] = t37;
    $[64] = t38;
    $[65] = t39;
  } else {
    t39 = $[65];
  }
  let t40;
  if ($[66] !== hasBusinessOwners || $[67] !== hasPermission || $[68] !== t) {
    t40 = hasPermission("manual_review") && <><Dropdown.Divider /><Dropdown.Group><Dropdown.GroupTitle>{t(_temp8)}</Dropdown.GroupTitle>{hasBusinessOwners && <Dropdown.Item onSelect={handleDialogOpen(ActionDialog.RequestMoreInfo)} shortcutHint={SHORTCUTS[ActionDialog.RequestMoreInfo]}>{t(_temp9)}</Dropdown.Item>}<Dropdown.Item onSelect={handleDialogOpen(ActionDialog.RunPlaybook)} shortcutHint={SHORTCUTS[ActionDialog.RunPlaybook]}>{t(_temp10)}</Dropdown.Item></Dropdown.Group></>;
    $[66] = hasBusinessOwners;
    $[67] = hasPermission;
    $[68] = t;
    $[69] = t40;
  } else {
    t40 = $[69];
  }
  let t41;
  if ($[70] !== isRiskOrCxOpsFirmEmployee || $[71] !== t || $[72] !== user?.isFirmEmployee) {
    t41 = user?.isFirmEmployee && <><Dropdown.Divider /><Dropdown.Group><Dropdown.GroupTitle>{t(_temp11)}</Dropdown.GroupTitle><Dropdown.Item onSelect={handleDialogOpen(ActionDialog.DeactivateEntity)}>{t(_temp12)}</Dropdown.Item>{isRiskOrCxOpsFirmEmployee && <><Dropdown.Item onSelect={handleDialogOpen(ActionDialog.Redact)}>{t(_temp13)}</Dropdown.Item><Dropdown.Item onSelect={handleDialogOpen(ActionDialog.RedactDocumentImages)}>{t(_temp14)}</Dropdown.Item></>}</Dropdown.Group></>;
    $[70] = isRiskOrCxOpsFirmEmployee;
    $[71] = t;
    $[72] = user?.isFirmEmployee;
    $[73] = t41;
  } else {
    t41 = $[73];
  }
  let t42;
  if ($[74] !== t39 || $[75] !== t40 || $[76] !== t41) {
    t42 = <Dropdown.Content align="end" sideOffset={8} className="min-w-[240px]">{t39}{t40}{t41}</Dropdown.Content>;
    $[74] = t39;
    $[75] = t40;
    $[76] = t41;
    $[77] = t42;
  } else {
    t42 = $[77];
  }
  let t43;
  if ($[78] !== dropdownOpen || $[79] !== t32 || $[80] !== t42) {
    t43 = <Dropdown.Root open={dropdownOpen} onOpenChange={setDropdownOpen}>{t32}{t42}</Dropdown.Root>;
    $[78] = dropdownOpen;
    $[79] = t32;
    $[80] = t42;
    $[81] = t43;
  } else {
    t43 = $[81];
  }
  let t44;
  if ($[82] !== hasPermission || $[83] !== openDialog) {
    t44 = hasPermission("write_entities") && <EditVaultDrawer open={openDialog === ActionDialog.EditVault} onClose={handleCloseDialog} />;
    $[82] = hasPermission;
    $[83] = openDialog;
    $[84] = t44;
  } else {
    t44 = $[84];
  }
  let t45;
  if ($[85] !== hasPermission || $[86] !== openDialog) {
    t45 = hasPermission("manual_review") && <RequestMoreInfo open={openDialog === ActionDialog.RequestMoreInfo} onClose={handleCloseDialog} />;
    $[85] = hasPermission;
    $[86] = openDialog;
    $[87] = t45;
  } else {
    t45 = $[87];
  }
  let t46;
  if ($[88] !== hasPermission || $[89] !== openDialog) {
    t46 = hasPermission("manual_review") && <RunPlaybookDialog entityKind="business" open={openDialog === ActionDialog.RunPlaybook} onClose={handleCloseDialog} />;
    $[88] = hasPermission;
    $[89] = openDialog;
    $[90] = t46;
  } else {
    t46 = $[90];
  }
  let t47;
  if ($[91] !== hasPermission || $[92] !== openDialog) {
    t47 = hasPermission("label_and_tag") && <EditTagsDialog open={openDialog === ActionDialog.EditTags} onClose={handleCloseDialog} />;
    $[91] = hasPermission;
    $[92] = openDialog;
    $[93] = t47;
  } else {
    t47 = $[93];
  }
  let t48;
  if ($[94] !== hasPermission || $[95] !== openDialog) {
    t48 = hasPermission("write_entities") && <UploadDocDialog open={openDialog === ActionDialog.UploadDoc} onClose={handleCloseDialog} />;
    $[94] = hasPermission;
    $[95] = openDialog;
    $[96] = t48;
  } else {
    t48 = $[96];
  }
  let t49;
  if ($[97] !== hasPermission || $[98] !== openDialog) {
    t49 = hasPermission("write_entities") && <RunVerificationChecksDialog open={openDialog === ActionDialog.RunVerificationChecks} onClose={handleCloseDialog} />;
    $[97] = hasPermission;
    $[98] = openDialog;
    $[99] = t49;
  } else {
    t49 = $[99];
  }
  const t50 = openDialog === ActionDialog.DeactivateEntity;
  let t51;
  if ($[100] !== t50) {
    t51 = <DeactivateEntityDialog open={t50} onClose={handleCloseDialog} />;
    $[100] = t50;
    $[101] = t51;
  } else {
    t51 = $[101];
  }
  let t52;
  if ($[102] !== isRiskOrCxOpsFirmEmployee || $[103] !== openDialog) {
    t52 = isRiskOrCxOpsFirmEmployee && <><RedactDialog kind="vault" open={openDialog === ActionDialog.Redact} onClose={handleCloseDialog} /><RedactDialog kind="documentImages" open={openDialog === ActionDialog.RedactDocumentImages} onClose={handleCloseDialog} /></>;
    $[102] = isRiskOrCxOpsFirmEmployee;
    $[103] = openDialog;
    $[104] = t52;
  } else {
    t52 = $[104];
  }
  let t53;
  if ($[105] !== t44 || $[106] !== t45 || $[107] !== t46 || $[108] !== t47 || $[109] !== t48 || $[110] !== t49 || $[111] !== t51 || $[112] !== t52) {
    t53 = <Suspense fallback={null}>{t44}{t45}{t46}{t47}{t48}{t49}{t51}{t52}</Suspense>;
    $[105] = t44;
    $[106] = t45;
    $[107] = t46;
    $[108] = t47;
    $[109] = t48;
    $[110] = t49;
    $[111] = t51;
    $[112] = t52;
    $[113] = t53;
  } else {
    t53 = $[113];
  }
  let t54;
  if ($[114] !== t43 || $[115] !== t53) {
    t54 = <>{t43}{t53}</>;
    $[114] = t43;
    $[115] = t53;
    $[116] = t54;
  } else {
    t54 = $[116];
  }
  return t54;
};
export default BusinessActions;
function _temp($) {
  return $.trigger;
}
function _temp2($_0) {
  return $_0.management.title;
}
function _temp3($_1) {
  return $_1.management.edit;
}
function _temp4($_2) {
  return $_2.management.editTags;
}
function _temp5($_3) {
  return $_3.management.addTags;
}
function _temp6($_4) {
  return $_4.management.uploadDocument;
}
function _temp7($_5) {
  return $_5.management.runVerificationChecks;
}
function _temp8($_6) {
  return $_6.requests.title;
}
function _temp9($_7) {
  return $_7.requests.requestInfo;
}
function _temp10($_8) {
  return $_8.requests.runPlaybook;
}
function _temp11($_9) {
  return $_9.internal.title;
}
function _temp12($_10) {
  return $_10.internal.deactivateEntity;
}
function _temp13($_11) {
  return $_11.internal.redact;
}
function _temp14($_12) {
  return $_12.internal.redactDocumentImages;
}